/* eslint-disable react-hooks/exhaustive-deps */
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { prepareHeaders } from "../user/user.api";
import { PaginationResults, TypeQuery } from "../user/user.type";
import { ApiBaseUrl } from "../../http";
import { QueryUrl, useLocationState } from '../../Utils';
import { IProprietaire, ProprietaireFormdata } from './proprietaire.type';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ICity } from '../logement/logement.type';

const ProprietaireApi = createApi({
    reducerPath: "proprietaireApi",
    tagTypes: ["proprietaire",'missionsByProprietaire', 'ville'],
    baseQuery: fetchBaseQuery({
        baseUrl: `${ApiBaseUrl}/api/`,
        prepareHeaders,
    }),
    endpoints: (build) => ({
        AddOrUpdateProprietaire: build.mutation<
            IProprietaire,
            { slug?: string; data: IProprietaire | FormData | Partial<IProprietaire> }
        >({
            query: ({ slug, data }) => ({
                url: slug ? `users/${slug}/` : `users/`,
                method: slug ? "PUT" : "POST",
                body: data,
            }),
            invalidatesTags: ["proprietaire"],
        }),
        getProprietaires: build.query<PaginationResults<IProprietaire>, TypeQuery>({
            query: (query) => QueryUrl("proprietaires", query),
            providesTags: ["proprietaire"],
        }),
        getProprietaire: build.query<IProprietaire, string>({
            query: (slug) => `proprietaires/${slug}`,
            providesTags: ["proprietaire"],
        }),
        deleteProprietaire: build.mutation<ProprietaireFormdata, string>({
            query: (slug) => ({
                url: `users/${slug}/`,
                method: "DELETE",
            }),
            invalidatesTags: ["proprietaire"],
        }),
        getLogementByProprietaire: build.query<IProprietaire[], string>({
            query: (slug) => `proprietaire/${slug}/logements`,
            providesTags: ["proprietaire"],
        }),
        getListMissionsByProprietaire: build.query<IProprietaire[], TypeQuery>({
            query: ({ slug, ...query }) => QueryUrl(`list/proprietaire/${slug}/missions/`, query),
            providesTags: ["missionsByProprietaire"],
        }),
        getProprietaireVilles: build.query<ICity[], TypeQuery>({
            query: (query) => QueryUrl("proprietaire/villes", query),
            providesTags: ["ville"],
        }),
    }),
});

export default ProprietaireApi

export const {
    useAddOrUpdateProprietaireMutation,
    useGetProprietairesQuery,
    useDeleteProprietaireMutation,
    useLazyGetProprietaireQuery,
    useGetLogementByProprietaireQuery,
    useGetListMissionsByProprietaireQuery,
    useGetProprietaireVillesQuery
} = ProprietaireApi;

export function useProprietaireFromLocation(): [
    IProprietaire,
    boolean,
    string,
    (id: string) => any
] {
    const itemState = useLocationState<IProprietaire | undefined>(undefined);
    const [item, setItem] = useState(itemState);
    const { slug } = useParams<{ slug: string }>();
    const [findById, { data, isError, isLoading }] = useLazyGetProprietaireQuery();
    const navigate = useNavigate();

    useEffect(() => {
        if (slug) {
            findById(slug);
        }
    }, [slug]);
    useEffect(() => {
        if (isError && !itemState) {
            navigate(-1);
        }
    }, [isError]);
    useEffect(() => {
        if (data) {
            setItem(data);
        }
    }, [data]);

    return [item as IProprietaire, isLoading, slug as string, findById];
}